import React, { Fragment, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Checkbox, Form, Grid, Header, Icon, Image, List, Message, Radio, Segment } from 'semantic-ui-react'
import { useEffect } from 'react'
import { find, map, size, filter, isNull, first, isEmpty } from 'lodash'
import routes from '../../routes/route-constants'
import { appendCommunity, classNames, getError, getParameterByName, setError } from '../../utils/helper'
import { calendarsActions } from '../../actions/calendarsActions'
import { PostCreateExcelCalendarRequest } from '../../api/requestObjects'
import { Trans, useTranslation } from 'react-i18next'
import ErrorLabel from '../partials/ErrorLabel'
import Role from '../partials/Role'
import AnimLoader from '../partials/AnimLoader'
import { messageBuilder } from '../../constants'
import { eventService } from '../../api'
import ConfirmModal from '../partials/ConfirmModal'
import { useDropzone } from 'react-dropzone'
import AdaptiveIcon from '../partials/timeline/AdaptiveIcon'
import AddUsersModal from '../partials/AddUsersModal'
import { push } from 'connected-react-router'
import i18next from 'i18next'

const CreateCalendarExcel = () => {

	const profile = useSelector(state => state.auth.profile)
	const navigation = useSelector(state => state.navigation)
	const calendarManagement = useSelector(state => state.calendars)
	const importErrors = useSelector(state => state.calendars?.importErrors)
	const personFilterChannels = useSelector(state => state.users?.member?.filters?.channels)

	const links = {
		'en': "https://docs.google.com/spreadsheets/d/1zCCLRmEqX-nvtenb17JP6YDzeTf7ggSl/edit?gid=2068058770#gid=2068058770",
		'es': "https://docs.google.com/spreadsheets/d/1b1lsq5gUMk9csPeANdTaiAj104ExuJoD/edit?usp=sharing&ouid=106613176972896477423&rtpof=true&sd=true",
		'af': "https://docs.google.com/spreadsheets/d/1ptUapUOJGHCtNtz-e42-ihS7Q42KP5OD/edit?gid=2068058770#gid=2068058770",
		'ar': "https://docs.google.com/spreadsheets/d/18G-usmtAGmJaulzYHoqYk5o9F6JUY5r9/edit",
		'fr': "https://docs.google.com/spreadsheets/d/1GbKfLS34f8cCOvIWCv06OIVxl_5XyvFK/edit",
	}

	const [calendarPayload, setCalendarPayload] = useState({
        file: null,
        personIds: [],
        channelIds: [],
		targetEveryone: false,
        communityId: navigation.communityId,
    })

	const [showAddUsers, setShowAddUsers] = useState(false)
	const [selectedUsers, setSelectedUsers] = useState({
		selected: [],
		users: [],
		tempOptions: [],
	})

	const [confirm, setConfirm] = useState({
        showModal: false,
        onConfirm: () => { },
        onClose: () => { },
        message: "",
        header: "",
        confirmText: "",
        cancelText: "",
    })
	
	const dispatch = useDispatch()
	const { t } = useTranslation()

	const [validationMessages, setValidationMessages] = useState([])
	const [isLoading, setLoading] = useState(false)

	const {
		open,
		getRootProps,
		getInputProps,
		acceptedFiles,
		fileRejections,
	} = useDropzone({
		multiple: false,
		noClick: true,
		noKeyboard: true,
		validator: (file) => {

			// Validate the Mime Type
			if (!["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "text/csv"].includes(file.type)) {
				return {
					code: "file-not-supported",
					message: t('requests.members.import.requiredFile')
				}
			}

			if (file.size > 50000000) {
				return {
					code: "file-too-large",
					message: t('messageBuilder.morphedTemplates.ImageMorphed.errors.message')
				}
			}

			return null
		}
	})
		

	useEffect(() => {
		document.body.classList.add('link-calendar')

		return () => {
			document.body.classList.remove('link-calendar')
		}
	}, [])
	
	useEffect(() => {
		
		if (!navigation.communityId) return;

		dispatch(calendarsActions.start())
		dispatch(calendarsActions.fetchCalendarFilters({ communityId: navigation.communityId }))
	}, [navigation.communityId])


	useEffect(() => {
		
		if (acceptedFiles.length === 0) return;

		// We will only accept one file at this time
		setCalendarPayload(prev => ({...prev, file: acceptedFiles[0]}))

	}, [acceptedFiles])
	
	useEffect(() => {
		
		if (fileRejections.length === 0) {
			setValidationMessages([])
			return;
		}
		
		const rejection = first(fileRejections)
		const error = fileRejections.length > 1 ? t("requestObjects.PostCreateNoticeRequest.file.required.atLeastOne") : (rejection.errors.length > 0 ? rejection.errors[0].message : t("error.errorOccurred"))
		setValidationMessages(setError("file", error))
		setCalendarPayload(prev => ({...prev, file: null}))

	}, [fileRejections])

	// Refresh events upon EventManager refreshData flag
	useEffect(() => {
		
		if (!calendarManagement.messages) return;
		
		if (!calendarManagement.messages.refreshEvents) return;

		dispatch(push(routes.private.MANAGE_EVENTS.replace(':communityId', navigation.communityId)))

	}, [calendarManagement?.messages])



	const handleNavigation = () => {
		// HARD Refresh
		window.location.href = appendCommunity(
			navigation.communityId,
			routes.private.MANAGE_CALENDARS
		)
	}

	const handleCloseConfirm = () => {
        setConfirm({
            showModal: false,
            onConfirm: () => { },
            onClose: () => { },
            message: "",
            header: "",
            confirmText: ""
        })
    }

	const _handleRecipientsClick = (_, {value}) => {
        setShowAddUsers(true)
    }

	const _handleAllTargetsClick = (_, {value}) => {
		
		// Remove all selections on recipients list
		const selections = [messageBuilder.TARGET_ALL_TAG_ID]
		
		setCalendarPayload(prev => ({
			...prev,
			channelIds: selections
		}))

		// We also need to clear out any direct recipients
		setSelectedUsers({
			selected: []
		})
	}

	const _handleLabelClick = (channelId) => {
		
		// Clicked on Target All, so we can ignore things
		if (channelId === messageBuilder.TARGET_ALL_TAG_ID) return;

		// Clicked on Add Individuals
		if (channelId === messageBuilder.ADD_INDIVIDUALS_TAG_ID) {
			setShowAddUsers(true)
			return;
		}
	}

	const handleAddUsers = ({ selection, options, tempOptions }) => {
		setSelectedUsers(prev => ({
			...prev,
			selected: selection,
			tempOptions: tempOptions,
		}))

		// Once users are added, we should now add the "Add Individuals" tag on the list, if it has not already been added
		const hasIndividuals = (calendarPayload.channelIds.indexOf(messageBuilder.ADD_INDIVIDUALS_TAG_ID) !== -1)

		// When no selection was made, we should remove the "Add Individuals" tag
		if (size(selection) === 0) {
			setCalendarPayload(prev => ({
				...prev,
				channelIds: filter(calendarPayload.channelIds, c => c !== messageBuilder.ADD_INDIVIDUALS_TAG_ID)
			}))
			return
		}

		if (!hasIndividuals) {
			setCalendarPayload(prev => ({
				...prev,
				// channelIds: calendarPayload.channelIds.concat([messageBuilder.ADD_INDIVIDUALS_TAG_ID])
				channelIds: [messageBuilder.ADD_INDIVIDUALS_TAG_ID]
			}))
		}
	}

	const handleChannelChange = ({name, value}) => {
		
		let selections = value

		// When the current selection has our "-901" id already, we should remove it and select everything else that was already selected
		if (value.indexOf(messageBuilder.TARGET_ALL_TAG_ID) > -1) {
			selections = filter(value, c => c !== messageBuilder.TARGET_ALL_TAG_ID)
		}

		setCalendarPayload(prev => ({
			...prev,
			[name]: selections
		}))

		// When the detected changes find out that "Add Individuals" is not longer in the list
		// we should also clear the selected individuals
		if (value.indexOf(messageBuilder.ADD_INDIVIDUALS_TAG_ID) === -1) {
			setSelectedUsers({
				selected: [],
				tempOptions: [],
			})
		}
	}

	const handlePostImportCalendar = () => {
		
		handleCloseConfirm()

        const request = new PostCreateExcelCalendarRequest(calendarPayload)
        const validator = request.validator()

        // Validate the request
        if (!validator.isValid) {
            setValidationMessages(validator.messages)
            return;
        }

        setValidationMessages([])
        dispatch(calendarsActions.start())
        dispatch(calendarsActions.postCreateExcelCalendar({
			...calendarPayload,
			channelIds: calendarPayload.channelIds.filter(x => x !== messageBuilder.ADD_INDIVIDUALS_TAG_ID),
			personIds: selectedUsers.selected,
		}))
    }

	const handleClearSelection = () => {
        setCalendarPayload(prev => ({
            ...prev,
            file: null,
        }))
    }

	const handleClose = () => {
		dispatch(push(routes.private.MANAGE_CALENDARS.replace(':communityId', navigation.communityId)))
	}

	return (
		<div className="link-calendar-full-page">
			
			<AnimLoader loading={calendarManagement.loading || isLoading} />

			{calendarManagement.messages && (
				<ConfirmModal 
					header={t('calendarsActions.createCalendar.excel.success.header')}
					open={true}
					onClose={handleClose}
					message={
						<Trans 
							components={{strong: <strong/>}}
							values={{
								email: profile.email ?? "(No Email)",
							}}
							i18nKey="modal.import.email.message"/>
					}
					showConfirm={false} />
			)}

			<ConfirmModal
				header={confirm.header}
				open={confirm.showModal}
				onClose={confirm.onClose}
				message={confirm.message}
				confirmClassName="primary danger"
				confirmText={confirm.confirmText}
				cancelText={confirm.cancelText}
				onConfirm={confirm.onConfirm}
				dismissible={false} />

			{/* Add Persons Modal */}
			<AddUsersModal
				isOpen={showAddUsers}
				tags={[]}
				data={{
					communityId: calendarPayload.communityId,
					channels: personFilterChannels ?? []
				}}
				onClose={() => setShowAddUsers(false)}
				title={t("modal.addUsers.title")}
				description={t("modal.addUsers.description")}
				currentSelection={selectedUsers.selected}
				tempOptions={selectedUsers.tempOptions ?? []}
				onAddUsers={handleAddUsers}
				fetchUsers={eventService.getTargetRecipients}/>

			<Grid className="calendar-items-container">
				<Grid.Row columns={1}>
					<Grid.Column width={16}>
						<h1>{t('calendars.settings.modal.excel.title')}</h1>
						{importErrors && (
							<Message error onDismiss={() => dispatch(calendarsActions.clearMessages())}>
								<Message.Header className='mg-bot-5'>{t('import.excel.error')}</Message.Header>
								<Message.Header className='mg-bot-5'>
									<strong>{t('import.excel.error.reupload')}</strong>
								</Message.Header>
								<Message.Content>
									{map(importErrors.errors, (error, row) => (
										<Message.Item key={row}>{t('import.excel.row-error', {row: row, error: [].concat(error).join(', ')})}</Message.Item>
									))}
									{isEmpty(importErrors.errors) && (
										<Message.Item>{t('error.import.excel.events.errorOccurred')}</Message.Item>
									)}
								</Message.Content>
							</Message>
						)}
					</Grid.Column>
				</Grid.Row>
				<Grid.Row columns={1} className="no-pad-bot ui form">
					<Grid.Column width={16}>
						<div className='w-full pb-2 pt-2'>
							<div className='text-align-right'>
								<Trans 
									i18nKey="import.events.excel.samples"
									components={{
										a: <a className='underline' target='_blank' rel="noopener noreferrer" href={links[i18next.language] ?? 'en'}/>,
									}} />
							</div>
						</div>
						<div {...getRootProps({ className: `dropzone import ${!getError("file", validationMessages).isValid ? 'error' : ''}` })}>
							<input {...getInputProps()} accept=".xlsx,.xls,.csv" />
							
							{isNull(calendarPayload.file) && (
								<Fragment>
									<p>
										<Icon className="no-margin green-text" name='file excel' /> 
										<Trans 
											i18nKey='import.excel.events.upload-file.caption' 
											components={{
												strong: <strong />
											}} />
									</p>
									<Button 
										size="tiny"
										className={`secondary ${!getError("file", validationMessages).isValid ? 'danger' : ''}`}
										onClick={open}>{t('import.excel.events.upload-file.chooseFile')}</Button>
								</Fragment>
							)}

							{!isNull(calendarPayload.file) && (
                                <Fragment>
                                    <div className='selected-file mt-4'>
                                        <AdaptiveIcon className="no-margin" url={calendarPayload.file.name} />
                                        <small className='mg-top-5 mg-bot-5'>{calendarPayload.file.name}</small>
                                        <Button.Group size='tiny' className='action-buttons'>
                                            <Button 
                                                title={t('import.members.removeSelectedFile')} 
                                                icon="trash" 
                                                className='secondary danger' 
                                                onClick={handleClearSelection} />
                                            <Button 
                                                title={t('messageBuilder.morphedTemplates.AttachmentMorphed.chooseFile')} 
                                                icon="upload" 
                                                className='secondary'
                                                onClick={open} />
                                        </Button.Group>
                                    </div>
                                </Fragment>
                            )}
						</div>
						<ErrorLabel error={getError("file", validationMessages) } />
					</Grid.Column>
				</Grid.Row>
				<Grid.Row columns={1} className="no-pad-bot ui form">
					<Grid.Column width={16}>
						<Form.Field
							required
							error={!getError("channelIds", validationMessages).isValid}
							className="input-container theme-color">

							<Role
								role={{
									id: "channelIds",
									name: t('calendar.ical.input.channels.title'),
									smallDescription: t('calendar.ical.input.channels.description')
								}}
								virtualised
								placeholder={t('calendar.input.channels.placeholder')}
								selected={calendarPayload.channelIds}
								onChange={handleChannelChange}
								options={[
									navigation?.community?.can_target_everyone ? { 
                                        onClick: _handleAllTargetsClick,
                                        key: messageBuilder.TARGET_ALL_TAG_ID, 
                                        value: messageBuilder.TARGET_ALL_TAG_ID, 
                                        text: t("messageBuilder.recipients.targetAll"),
                                        className: "special-actions",
                                    } : false,
									{
										onClick: _handleRecipientsClick,
										className: "special-actions",
										key: messageBuilder.ADD_INDIVIDUALS_TAG_ID, 
										value: messageBuilder.ADD_INDIVIDUALS_TAG_ID, 
										text: t("modal.addUsers.button.addIndividuals") + (selectedUsers.selected.length > 0 ? `  (${selectedUsers.selected.length})` : "")
									}
								].concat(calendarManagement?.filters?.channels ?? []).filter(Boolean)}
								onLabelClick={(_, data) => _handleLabelClick(data.value)} />
						</Form.Field>
					</Grid.Column>
				</Grid.Row>
				<Grid.Row columns="equal" className='mg-top-20'>
					<Grid.Column width={16} textAlign="right">
						<Button
							onClick={handleNavigation}
							className="secondary mg-right-5">
							{t('general.cancel')}
						</Button>
						<Button
							disabled={calendarManagement.loading}
							onClick={handlePostImportCalendar}
							className="primary no-margin">
							{t('calendars.excel.settings.modal.confirm.text')}
						</Button>
					</Grid.Column>
				</Grid.Row>
			</Grid>
		</div>
	)
}

export default CreateCalendarExcel
